import { Icon } from '@iconify/react';
import { useRef, useState } from 'react';

import flagVariantOutline from '@iconify/icons-mdi/flag-variant-outline';
import flagVariant from '@iconify/icons-mdi/flag-variant';
// material
import { MenuItem, ListItemIcon, ListItemText, Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { reportTripsActions } from '../../store';
import { editItem } from '../../fetch';
import updateFlightList from '../flightList/updateFlightList';
import MenuPopover from '../MenuPopover';
import { useTranslation } from '../../common/LocalizationProvider';
import { selectSession } from '../../createSelectorUtils';

// ----------------------------------------------------------------------

FlightReviewFlag.propTypes = {
  reportTrip: PropTypes.object.isRequired,
  setIsOpenSpeedDial: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export default function FlightReviewFlag({ reportTrip, setIsOpenSpeedDial, disabled }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const ref = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const session = useSelector(selectSession);

  const [isOpen, setIsOpen] = useState(false);

  const handleAddFlag = async (value) => {
    if (!disabled) {
      const body = {
        id: reportTrip.affectationVolsId,
        deviceId: reportTrip.deviceId,
        piloteId: reportTrip.driverUniqueId,
        instructeurId: reportTrip.instructeurUniqueId,
        startTime: reportTrip.startTime,
        endTime: reportTrip.endTime,
        groupId: reportTrip.groupId,
        attributes: {},
        comment: reportTrip.commentAffectationVols,
        flag: value,
        favorite: reportTrip.favoriteAffectationVols,
        remarquableTrace: reportTrip.pointRemarquableTrace > 0
      };

      const response = await editItem('affectationVols', body.id, body);
      if (response.ok) {
        enqueueSnackbar(t('FL_Flags_Success'), {
          variant: 'success'
        });
        dispatch(reportTripsActions.update(await updateFlightList(body)));
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }
    }
    setIsOpen(false);
    setIsOpenSpeedDial(false);
  };

  const flagIcon = () => {
    switch (reportTrip.flagAffectationVols) {
      case 'revoir':
        return { icon: flagVariant, color: '#FF9BFF' };
      case 'resolu':
        return { icon: flagVariant, color: '#00FF00' };
      case 'etude':
        return { icon: flagVariant, color: '#FEC802' };
      case 'signaler':
        return { icon: flagVariant, color: '#FF0000' };
      case 'concour':
        return { icon: flagVariant, color: '#9900FF' };

      default:
        return { icon: flagVariantOutline, color: '' };
    }
  };
  return (
    <>
      <MenuItem
        sx={{ color: 'text.secondary' }}
        onClick={() => setIsOpen(true)}
        ref={ref}
        disabled={disabled}
      >
        <Icon icon={flagIcon().icon} width={23} height={23} ref={ref} color={flagIcon().color} />

        <ListItemText
          primary={t('FR_Tooltip_AddFlag')}
          primaryTypographyProps={{ variant: 'body2' }}
        />
      </MenuItem>

      <MenuPopover
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        sx={{ width: 200 }}
      >
        <Box>
          <MenuItem onClick={() => handleAddFlag('concour')} sx={{ color: 'text.secondary' }}>
            <ListItemIcon key="concour">
              <Icon icon={flagVariant} width={24} height={24} color="#9900FF" />
            </ListItemIcon>
            <ListItemText
              primary={t('FL_Flags_Competition')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
          <MenuItem onClick={() => handleAddFlag('revoir')} sx={{ color: 'text.secondary' }}>
            <ListItemIcon key="revoir">
              <Icon icon={flagVariant} width={24} height={24} color="#FF9BFF" />
            </ListItemIcon>
            <ListItemText
              primary={t('FL_Flags_Review')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
          {session.administrator && (
            <MenuItem onClick={() => handleAddFlag('resolu')} sx={{ color: 'text.secondary' }}>
              <ListItemIcon key="resolu">
                <Icon icon={flagVariant} width={24} height={24} color="#00FF00" />
              </ListItemIcon>
              <ListItemText
                primary={t('FL_Flags_Solve')}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </MenuItem>
          )}
          {session.administrator && (
            <MenuItem onClick={() => handleAddFlag('etude')} sx={{ color: 'text.secondary' }}>
              <ListItemIcon key="etude">
                <Icon icon={flagVariant} width={24} height={24} color="#FEC802" />
              </ListItemIcon>
              <ListItemText
                primary={t('FL_Flags_Progress')}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </MenuItem>
          )}
          <MenuItem onClick={() => handleAddFlag('signaler')} sx={{ color: 'text.secondary' }}>
            <ListItemIcon key="signaler">
              <Icon icon={flagVariant} width={24} height={24} color="#FF0000" />
            </ListItemIcon>
            <ListItemText
              primary={t('FL_Flags_Anomaly')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
          <MenuItem onClick={() => handleAddFlag(null)} sx={{ color: 'text.secondary' }}>
            <ListItemIcon>
              <Icon icon={flagVariantOutline} width={24} height={24} />
            </ListItemIcon>
            <ListItemText
              primary={t('FL_Flags_Remove')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        </Box>
      </MenuPopover>
    </>
  );
}
